import { inject, Injectable } from '@angular/core';

import { GetFundingDataInput } from 'src/app/models/configurators-2d/tunnel/get-funding-data-input';
import { TunnelDataStore } from 'src/app/stores/tunnel/tunnel-data.store';
import { FeatureSettingsService } from '../feature-settings/feature-settings.service';
import { isFundingStateEmpty } from '../helpers/funding.helper';

/**
 * Implémenté structurellement par les cinq services de tunnel porteurs d'un financement
 * (cuisine, rangement, SDB Kentia, SDB HBM, menuiserie) : ils passent simplement `this`.
 */
export interface FundingComputer {
  computeFunding(input: GetFundingDataInput): Promise<void>;
}

/**
 * Réhydratation du financement à la réouverture d'un projet, partagée par les tunnels.
 *
 * Ne peut pas vivre dans `BaseAppService` : celui-ci n'injecte ni le store ni les feature settings,
 * et quatre de ses neuf sous-classes (portes de placard, lames de terrasse, clôture, plan de
 * travail) n'ont pas de financement — un membre abstrait les forcerait à l'implémenter pour rien.
 */
@Injectable({ providedIn: 'root' })
export class FundingRehydrationService {
  private readonly tunnelDataStore = inject(TunnelDataStore);
  private readonly featureSettingsService = inject(FeatureSettingsService);

  /**
   * Recalcule une fois le financement quand `Tunnel/pricing` n'a pas ramené les offres persistées :
   * le mapper retombe alors sur un funding vide, que la Validation affiche tel quel en
   * « 0,00 € / mois durant 0 mois » si le vendeur clique directement l'onglet en sautant l'étape
   * Financement.
   *
   * No-op dès que l'API renvoie les offres. Le garde sur le feature setting est nécessaire : sans
   * lui, `computeFunding` prendrait son repli `disableCurrentFunding()` + rechargement du pricing,
   * soit un appel inutile de plus quand le financement est coupé.
   */
  public async rehydrateIfNeeded(tunnelConfigurationId: string, computer: FundingComputer): Promise<void> {
    if (!tunnelConfigurationId) return;
    if (!(await this.featureSettingsService.isFinancementEnabled())) return;

    // Valeur autoritaire du store : le champ `currentFunding` des services n'est pas remis à
    // `undefined` par `resetCurrentFunding()`, leur souscription filtrant sur `if (funding)`.
    const funding = this.tunnelDataStore.getCurrentStateValue().currentFunding;
    if (!isFundingStateEmpty(funding)) return;

    await computer.computeFunding({
      tunnelConfigurationId,
      downPaymentAmount: funding?.downPayment ?? 0,
      shouldRecalculateFinancing: true,
    });
  }
}
